import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { UserService } from '~/Services'
import { refreshAccessToken } from '~/Services/AuthIntercept'
import { UserInviteStatus, UserPreferencesData } from '~/Services/UserService.d'
import { useAppDispatch } from '~/Store/hooks'
import { WaitOverlayActions } from '~/Store/Overlays'
import { UserActions, UserSelectors } from '~/Store/User'
import { WaitOverlay } from '../Overlays'
import { Utils } from '~/Utils'
import styles from '~/Views/Main/Main.module.less'
import { RootState } from '~/Store/reducers'
import { SYSTEM_CONSTANT } from '~/constants/system-constant'

interface LoginWorkFlowProps {
  children: React.ReactNode
}

export const LoginWorkFlow = ({ children }: LoginWorkFlowProps) => {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const authenticated = useSelector(UserSelectors.isAuthenticated)
  const organizations = useSelector((state: RootState) =>
    UserSelectors.getUserOrganizationsByStatus(state, [
      UserInviteStatus.Accepted,
    ]),
  )
  const preferences = useSelector(UserSelectors.getUserPreferences)
  const refreshToken = useSelector(UserSelectors.getRefreshToken)
  const token = useSelector(UserSelectors.getToken)
  const currentUser = useSelector(UserSelectors.getCurrentUser)
  const roles = useSelector((state: RootState) => state.user.roles)
  const orgRoles = useSelector((state: RootState) => state.user.orgRoles)
  const authenticatedWithOrg = useSelector(
    UserSelectors.isAuthenticatedWithOrg,
  )

  const [loginProcess, setLoginProcess] = useState(true)

  const waitOverlayName = 'loginFlowOverlay'

  const fetchUserProfile = async () => {
    if (!authenticated) return
    try {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: true }),
      )
      const res = await UserService.Get.fetchCurrentUser()
      dispatch(UserActions.setCurrentUser(res.data))
    } catch {
      setLoginProcess(false)
    } finally {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: false }),
      )
    }
  }

  const fetchOrganizations = async () => {
    if (!authenticated || !currentUser || currentUser.forcePasswordChange)
      return
    try {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: true }),
      )
      const res = await UserService.Get.fetchCurrentUserOrganizations()
      dispatch(UserActions.setUserOrganizations(res.data))
      if (res.data.length === 0) setLoginProcess(false)
    } catch {
      setLoginProcess(false)
    } finally {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: false }),
      )
    }
  }

  useEffect(() => {
    fetchUserProfile()
  }, [authenticated])

  const forceResetToken = async () => {
    if (!currentUser || !currentUser.forcePasswordChange) return
    try {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: true }),
      )
      const res: any = await UserService.Get.fetchTokenForPasswordReset()
      const token = Utils.encodeRFC5987ValueChars(res.data)
      const cb = encodeURIComponent(
        `${SYSTEM_CONSTANT.LICENSE_MANAGER_URL}/users/${currentUser.id}/password/reset?token=${token}`,
      )
      history.push(`/passwordreset?cb=${cb}`)
    } finally {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: false }),
      )
    }
  }

  useEffect(() => {
    forceResetToken()
  }, [currentUser])

  useEffect(() => {
    fetchOrganizations()
  }, [authenticated, currentUser])

  const sanitizeDefaultOrganization = async () => {
    if (!organizations.length) return
    try {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: true }),
      )

      const res: any = await UserService.Get.fetchCurrentUserPreferences()
      const data: UserPreferencesData = res.data

      let defaultOrgId = data.defaultOrganization
      const exists =
        defaultOrgId &&
        organizations.some(o => o.guid === defaultOrgId)

      if (!exists) {
        defaultOrgId = organizations[0].guid
        await UserService.Post.currentUserPreferences({
          ...data,
          defaultOrganization: defaultOrgId,
        })
      }

      dispatch(
        UserActions.setUserPreferences({
          ...data,
          defaultOrganization: defaultOrgId,
        }),
      )
    } catch {
      const fallbackOrg = organizations[0]
      dispatch(
        UserActions.setUserPreferences({
          settings: '',
          defaultOrganization: fallbackOrg.guid,
        }),
      )
    } finally {
      setLoginProcess(false)
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: false }),
      )
    }
  }

  useEffect(() => {
    sanitizeDefaultOrganization()
  }, [organizations.length])

  const organizationRefreshToken = async () => {
    if (!refreshToken || !preferences?.defaultOrganization) return
    setLoginProcess(true)
    dispatch(
      WaitOverlayActions.setVisible({ name: waitOverlayName, value: true }),
    )
    await refreshAccessToken(refreshToken)
    dispatch(
      WaitOverlayActions.setVisible({ name: waitOverlayName, value: false }),
    )
    setLoginProcess(false)
  }

  useEffect(() => {
    organizationRefreshToken()
  }, [preferences?.defaultOrganization])

  useEffect(() => {
    Utils.setLocalStorage('token', token)
  }, [token])

  useEffect(() => {
    if (!authenticatedWithOrg) return

    const authorized =
      roles.includes('System') ||
      orgRoles.includes('Admin') ||
      orgRoles.includes('SuperUser')

    if (!authorized && history.location.pathname !== '/unauthorized') {
      setLoginProcess(false)
      history.push('/unauthorized')
    }
  }, [authenticatedWithOrg, roles, orgRoles])

  return (
    <WaitOverlay
      name={waitOverlayName}
      size="large"
      wrapperClassName={styles.FullOverlay}
    >
      {!loginProcess && children}
    </WaitOverlay>
  )
}
